<script lang="ts" setup>
// ================================
// IMPORTS
// ================================

// Vue
import { ref, onMounted, computed, onBeforeUnmount } from "vue"
import { useI18n } from "vue-i18n"

// VueUse
import { useEventBus } from "@vueuse/core"

// Composables
import { useFetchWrapper } from "@core/composables"

// Consent
import {
    CONSENT_PAGE_AFTER_SOURCE_MULTI_APPEND_BUS_KEY,
    CONSENT_REQUESTS_RELOAD_BUS_KEY,
} from "./consent.events"

// Services
import { ConsentRequestService } from "@core/services"

// Types
import { ConsentRequest } from "@types"

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Demande en cours",
            sign_consent: "Signature de consentement",
            tooltip_sign_consent:
                "Demande d'accès aux données de consommation.",
            extend_consent: "Extension de consentement",
            tooltip_extend_consent:
                "Demande de prolongement d'accès aux données de consommation.",
            change_owner_consent: "Changement de titulaire",
            tooltip_change_owner_consent:
                "Demande de changement de titulaire du contrat du fournisseur.",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

// ================================
// REACTIVE STATE
// ================================

const consentRequestService = new ConsentRequestService()

const rowsPerPage = ref(10)
const expandedRows = ref<Record<string, boolean>>({})

const optionsRequestType = [
    {
        name: t("sign_consent"),
        value: "SIGN",
        tooltip: t("tooltip_sign_consent"),
    },
    {
        name: t("extend_consent"),
        value: "EXTEND",
        tooltip: t("tooltip_extend_consent"),
    },
    {
        name: t("change_owner_consent"),
        value: "CHANGE_OWNER",
        tooltip: t("tooltip_change_owner_consent"),
    },
]

const selectedRequestType = ref<"SIGN" | "EXTEND" | "CHANGE_OWNER">("SIGN")

const consentRequests = ref<ConsentRequest[] | null>(null)

const {
    fetch: fetchConsentRequests,
    isFetching: isFetchingConsentRequests,
    hasError: hasErrorConsentRequests,
    error: errorConsentRequests,
} = useFetchWrapper(async () => {
    const result = await consentRequestService.getConsentRequests()
    consentRequests.value = result
})

/**
 * Rechargement de la liste lorsqu’une demande est créée ailleurs (ex. prolongement / changement titulaire depuis ConsentTable).
 */
const busConsentRequestsReload = useEventBus<void>(
    CONSENT_REQUESTS_RELOAD_BUS_KEY
)

const unsubscribeConsentRequestsReload = busConsentRequestsReload.on(() => {
    void fetchConsentRequests()
})

const busAfterSourceMultiAppend = useEventBus<void>(
    CONSENT_PAGE_AFTER_SOURCE_MULTI_APPEND_BUS_KEY
)

const unsubscribeAfterSourceMultiAppend = busAfterSourceMultiAppend.on(() => {
    void fetchConsentRequests()
})

// ================================
// LIFECYCLE
// ================================

onMounted(() => {
    fetchConsentRequests()
})

onBeforeUnmount(() => {
    unsubscribeConsentRequestsReload()
    unsubscribeAfterSourceMultiAppend()
})

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Affiche la colonne « Bâtiment associé » uniquement pour l’onglet signature
 * (SIGN / SHARE), où l’API expose le bâtiment — pas pour EXTEND ni CHANGE_OWNER.
 */
const isConsentRequestSign = computed(() => {
    return selectedRequestType.value === "SIGN"
})

/**
 * Liste filtrée des demandes de consentement selon le type sélectionné
 */
const getCurrentConsentRequest = computed(() => {
    if (!consentRequests.value) return []

    return consentRequests.value.filter((request: ConsentRequest) => {
        return matchesRequestType(request)
    })
})

// ================================
// METHODS
// ================================

/**
 * Vérifie si la demande correspond au type de demande sélectionné
 * @param request - Demande de consentement à vérifier
 * @returns True si la demande correspond au type sélectionné
 */
const matchesRequestType = (request: ConsentRequest) => {
    if (selectedRequestType.value === "SIGN") {
        return request.type === "SIGN" || request.type === "SHARE"
    }
    return request.type === selectedRequestType.value
}

/**
 * Supprime une demande de consentement du tableau local sans refaire un appel API
 * @param consentRequestId - ID de la demande de consentement à supprimer
 */
function onDeleteConsent(consentRequestId: string): void {
    if (!consentRequests.value) return

    consentRequests.value = consentRequests.value.filter(
        (request: ConsentRequest) => request._id !== consentRequestId
    )
}

/**
 * Met à jour une demande dans la liste (ex. après envoi d’e-mail change-owner) sans recharger toute la liste.
 */
function onUpdateConsentRequest(updated: ConsentRequest): void {
    if (!consentRequests.value || !updated._id) return

    const idx = consentRequests.value.findIndex((r) => r._id === updated._id)
    if (idx === -1) return

    consentRequests.value = [
        ...consentRequests.value.slice(0, idx),
        updated,
        ...consentRequests.value.slice(idx + 1),
    ]
}
</script>

<template>
    <BaseTitledCard class="w-full flex flex-col">
        <template #header>
            {{ t("title") }}
        </template>
        <template #content>
            <div class="flex flex-col gap-12">
                <!-- Sélection pour le type de demande: SIGN, EXTEND, CHANGE_OWNER -->
                <BaseSelectButton
                    v-model="selectedRequestType"
                    :options="optionsRequestType"
                    option-label="name"
                    option-value="value"
                    size="small"
                >
                    <template #option="slotProps">
                        <div class="flex items-center gap-2">
                            <span>{{ slotProps.option.name }}</span>
                            <BaseIcon
                                class="flex"
                                icon="pi pi-exclamation-circle"
                                v-tooltip.top="slotProps.option.tooltip"
                            />
                        </div>
                    </template>
                </BaseSelectButton>

                <!-- Loading state -->
                <BaseSkeleton
                    width="100%"
                    height="30rem"
                    class="mx-auto"
                    v-if="isFetchingConsentRequests"
                />

                <!-- Error state -->
                <BaseMessage
                    severity="error"
                    v-else-if="hasErrorConsentRequests"
                >
                    {{
                        errorConsentRequests?.message ||
                        "Une erreur est survenue lors du chargement de la demande"
                    }}
                </BaseMessage>

                <!-- Data table -->
                <ConsentRequestTable
                    v-else-if="getCurrentConsentRequest?.length"
                    :consent-requests="getCurrentConsentRequest"
                    :is-consent-request-sign="isConsentRequestSign"
                    :rows-per-page="rowsPerPage"
                    v-model:expanded-rows="expandedRows"
                    @reload:consent="fetchConsentRequests"
                    @delete:consent="onDeleteConsent"
                    @update:consent-request="onUpdateConsentRequest"
                />

                <!-- Empty state -->
                <BaseMessage v-else>
                    Aucune demande de consentement trouvée.
                </BaseMessage>
            </div>
        </template>
    </BaseTitledCard>
</template>

<style scoped></style>
